import { useId } from 'react';
import { PAGE_SIZE_OPTIONS, pageWindow } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Icon, Select, cn } from '@mypoz/ui';

import { useFormat } from '@/lib/format.js';

export interface PagerProps {
  /** Already clamped by the caller. */
  page: number;
  pageCount: number;
  /** How many rows this page is showing, for the "x–y of z" line. */
  rangeStart: number;
  rangeEnd: number;
  total: number;
  onGo: (page: number) => void;
  /** The nav's accessible name. Defaults to the package list's; each list passes its own. */
  label?: string;
  /**
   * Rows per page, with `onPageSize`: shows the "Rows per page" picker (MYP-126). Lists without a
   * choice — the order pickers, Notifications, the Wallet history — leave both out.
   */
  pageSize?: number;
  onPageSize?: (size: number) => void;
}

const BOX =
  'flex size-11 shrink-0 items-center justify-center rounded-md text-sm tabular-nums transition-colors lg:size-9 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';

function ArrowButton({
  label,
  icon,
  disabled,
  onClick,
}: {
  label: string;
  icon: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        BOX,
        // Clipped so the icon's ligature text can never spill over a neighbour if the icon font
        // has not loaded.
        'overflow-hidden border border-line text-ink hover:bg-surface-alt',
        'disabled:cursor-not-allowed disabled:text-muted-soft disabled:hover:bg-transparent',
      )}
    >
      <Icon name={icon} size={20} />
    </button>
  );
}

/**
 * The page control under a paged list — shared by Packages, Orders, the two order pickers,
 * Notifications and the Wallet's top-up history.
 *
 * MYP-126 (the mentor's reference design): first, previous, the page numbers with `…` gaps, next
 * and last, plus an optional "Rows per page" picker. The active page is filled in brand red and
 * marked `aria-current`, so it is told apart by more than colour. On a phone the numbers would not
 * fit, so the strip becomes "first, previous, Page 3 of 16, next, last" — the same moves, every
 * target still 44px.
 *
 * Renders nothing when there is nothing to move between and nothing to choose: a control that can
 * never do anything is noise. With a picker it stays as long as the list is longer than the
 * smallest page size, so a customer who picked 50 can always pick 10 again.
 */
export function Pager({
  page,
  pageCount,
  rangeStart,
  rangeEnd,
  total,
  onGo,
  label,
  pageSize,
  onPageSize,
}: PagerProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const sizeId = useId();

  const withPicker = pageSize !== undefined && onPageSize !== undefined;
  const smallest = PAGE_SIZE_OPTIONS[0];
  if (pageCount <= 1 && !(withPicker && total > smallest)) return null;

  const slots = pageWindow(page, pageCount);
  const first = page <= 1;
  const last = page >= pageCount;

  return (
    <nav
      aria-label={label ?? t('packages.pager.label')}
      className="mt-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3"
    >
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <p className="text-sm text-muted tabular-nums">
          {t('packages.pager.range', {
            start: fmt.number(rangeStart),
            end: fmt.number(rangeEnd),
            total: fmt.number(total),
          })}
        </p>

        {withPicker ? (
          <div className="flex items-center gap-2">
            <label htmlFor={sizeId} className="text-sm whitespace-nowrap text-muted">
              {t('packages.pager.perPage')}
            </label>
            <Select
              id={sizeId}
              value={String(pageSize)}
              onChange={(next) => onPageSize(Number(next))}
              options={PAGE_SIZE_OPTIONS.map((size) => ({
                value: String(size),
                label: fmt.number(size),
              }))}
              className="w-20"
            />
          </div>
        ) : null}
      </div>

      {pageCount > 1 ? (
        <div className="flex items-center gap-1.5">
          <ArrowButton
            label={t('packages.pager.first')}
            icon="keyboard_double_arrow_left"
            disabled={first}
            onClick={() => onGo(1)}
          />
          <ArrowButton
            label={t('packages.pager.previous')}
            icon="chevron_left"
            disabled={first}
            onClick={() => onGo(page - 1)}
          />

          {/* Phone: the position in words — the numbers would not fit. */}
          <p className="px-2 text-sm whitespace-nowrap text-ink tabular-nums sm:hidden">
            {t('packages.pager.page', {
              page: fmt.number(page),
              pageCount: fmt.number(pageCount),
            })}
          </p>

          <ol className="hidden items-center gap-1.5 sm:flex">
            {slots.map((slot, index) =>
              slot === 'gap' ? (
                <li
                  key={`gap-${index}`}
                  aria-hidden="true"
                  className="flex w-6 justify-center text-sm text-muted"
                >
                  …
                </li>
              ) : (
                <li key={slot}>
                  <button
                    type="button"
                    onClick={() => onGo(slot)}
                    aria-label={t('packages.pager.goTo', { page: fmt.number(slot) })}
                    aria-current={slot === page ? 'page' : undefined}
                    className={cn(
                      BOX,
                      slot === page
                        ? 'bg-brand font-semibold text-white'
                        : 'border border-line text-ink hover:bg-surface-alt',
                    )}
                  >
                    {fmt.number(slot)}
                  </button>
                </li>
              ),
            )}
          </ol>

          <ArrowButton
            label={t('packages.pager.next')}
            icon="chevron_right"
            disabled={last}
            onClick={() => onGo(page + 1)}
          />
          <ArrowButton
            label={t('packages.pager.last')}
            icon="keyboard_double_arrow_right"
            disabled={last}
            onClick={() => onGo(pageCount)}
          />
        </div>
      ) : null}
    </nav>
  );
}
